/**
 * The time one request may take, its body included.
 *
 * A browser request with no bound waits as long as the service does, which is how a screen stays on
 * "Loading…" for ever. The deadline aborts the request through its signal and also ends the wait
 * itself, so a fetcher that ignores the signal (or a body that stalls after the headers arrived) still
 * ends in time.
 */
export class Deadline {
	#controller = new AbortController();
	#timer: ReturnType<typeof setTimeout>;
	#expired: Promise<never>;

	/** The signal to hand to `fetch` */
	get signal(): AbortSignal {
		return this.#controller.signal;
	}

	/** Whether the time ran out */
	get passed(): boolean {
		return this.#controller.signal.aborted;
	}

	/**
	 * @param wait Milliseconds the whole request may take
	 */
	constructor(wait: number) {
		let reject: (reason: unknown) => void = () => undefined;
		this.#expired = new Promise<never>((_resolve, fail) => (reject = fail));
		// Nothing may be left unhandled when the request settles first
		this.#expired.catch(() => undefined);
		this.#timer = setTimeout(() => {
			const reason = new DOMException('The request took too long', 'TimeoutError');
			this.#controller.abort(reason);
			reject(reason);
		}, wait);
	}

	/**
	 * @returns What the work produced, or a rejection once the deadline passes
	 */
	race<T>(work: Promise<T>): Promise<T> {
		return Promise.race([work, this.#expired]);
	}

	/** Releases the timer once the request is over */
	clear(): void {
		clearTimeout(this.#timer);
	}
}
